<template>
  <section :class="$style.sectionRoot">
    <header
      :class="$style.sectionHeader"
      data-test-id="section-header"
    >
      {{ directoryType }} Sync
    </header>
    <div :class="$style.sectionContent">
      <div v-if="isItemBeingCreated">
        <p>
          JumpCloud can act as the authoritative source of identity and control to provision,
          deprovision, and synchronize user accounts and profile data in {{ directoryType }}. To
          begin, import your current user base from {{ directoryType }}.
        </p>
        <FlatActionButton
          :action="handleAuthorizeSync"
        >
          authorize sync
        </FlatActionButton>
      </div>
      <div v-if="isItemBeingUpdated">
        <p>
          JumpCloud is acting as the authoritative source of identity and control to provision,
          deprovision, and synchronize user accounts and profile data in {{ directoryType }}.
          You may reactivate your connection to renew authorization tokens or resolve connection
          issues.
        </p>
        <p v-if="expiryDate">
          <TimerIcon :class="$style.timerIcon" />
          {{ expiryDateDisplay }}
        </p>
        <FlatActionButton
          :action="handleReactivate"
          :class="$style.reactivateButton"
        >
          reactivate sync
        </FlatActionButton>
        <FlatActionButton
          :action="confirmDeactivate"
          :buttonClass="['btnDefault', 'btnAlert']"
        >
          deactivate sync
        </FlatActionButton>
        <p
          v-if="parentType === gSuite || parentType === office365"
          :class="$style.info"
        >
          Don't authorize the same {{ directoryType }} directory more than once.
          <a
            :href="kbUrl"
            rel="noopener noreferrer"
            target="_blank"
          >
            <em>{{ kbLinkText }}</em>
          </a>
        </p>
      </div>
    </div>
    <slot :class="$style.sectionContent" />
  </section>
</template>
<script>
import {
  DirectoryNameConsts,
  DirectoryTypeConsts,
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import DirectoryActionsMixin from 'client/directories/components/DirectoryActionsMixin';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus, { ModalEvents } from 'client/components/Modal/ModalEventBus';
import TimerIcon from 'public/img/icons/timer.svg?inline';
import moment from 'moment';

const { gSuite, office365 } = ItemTypeConsts;
const directoryCollection = StoreModuleMap[ItemTypeConsts.directory];

export default {
  name: 'DirectorySync',

  components: {
    FlatActionButton,
    TimerIcon,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    DirectoryActionsMixin,
  ],

  props: {
    activateUrl: {
      type: String,
      default: '',
    },
    deactivateAction: {
      type: Function,
      default: null,
    },
    deactivateUrl: {
      type: String,
      default: '',
    },
    isItemBeingCreated: {
      type: Boolean,
      default: false,
    },
    isItemBeingUpdated: {
      type: Boolean,
      deafult: false,
    },
    parentType: {
      type: String,
      default: '',
    },
    reactivateAction: {
      type: Function,
      default: null,
    },
    reactivateUrl: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      gSuite,
      kbUrl: this.parentType === gSuite ? KbArticleUrls.gSuiteImport : KbArticleUrls.officeImport,
      office365,
      kbLinkText: this.parentType === gSuite ? 'Google Workspace Integration Guide' : 'Learn More',
    };
  },

  computed: {
    ...mapGetters(directoryCollection, {
      items: 'getCollection',
    }),

    directoryType() {
      return DirectoryNameConsts[this.parentType];
    },

    expiryDate() {
      const item = this.items.find(i => i.id === this.parent.id);
      const seconds = get(item, 'oAuthStatus.expiry.seconds', null);
      return seconds ? moment.unix(seconds) : null;
    },

    expiryDateDisplay() {
      if (this.expiryDate) {
        if (this.expiryDate.isBefore()) {
          return 'Token expired';
        }

        return `Token expires ${this.expiryDate.utc().format('LLL')} (UTC)`;
      }

      return null;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options, true);
  },

  methods: {
    confirmDeactivate() {
      EventHelper.triggerDeactivateSyncClicked({
        resourceName: this.parentType,
      });

      const { displayName, directoryType } = this;
      const name = displayName || directoryType;
      let modalBodyText = '';
      const directoryName = DirectoryNameConsts[DirectoryTypeConsts.office365];

      if (directoryType === directoryName) {
        modalBodyText = `Deactivation will discontinue synchronization between JumpCloud and **${name}**. Any users and groups still bound to this ${directoryType} Directory will be unbound within JumpCloud, but this will not affect their current state in either Directory.\n\nThis will permanently delete this instance from JumpCloud and can’t be restored. Are you sure you want to continue?`;
      } else {
        modalBodyText = `Deactivation will discontinue synchronization between JumpCloud and **${name}**, but this will not affect the current status of the ${directoryType} users in either Directory. Users and Groups will be unbound from the ${name} Directory within JumpCloud. Are you sure you want to continue?`;
      }
      const componentProps = {
        mdTemplate: modalBodyText,
        cancelText: 'cancel',
        continueText: 'Deactivate Sync',
        handleCancel: () => ModalEventBus.close(),
        handleContinue: () => {
          EventHelper.triggerDeactivateSyncConfirmed({
            resourceName: this.parentType,
          });
          ModalEventBus.$once(ModalEvents.transitionEnd, () => this.handleContinueDelete());
          ModalEventBus.close();
        },
        headerText: `Deactivate ${directoryType} Synchronization`,
        mainActionAttention: true,
        showClose: true,
      };

      ModalEventBus.open({
        isLargeModal: false,
        component: 'ConfirmationModal',
        componentProps,
      });
    },

    handleAuthorizeSync() {
      EventHelper.triggerAuthorizeSyncClicked({
        resourceName: this.parentType,
      });
      this.openExternalLink(this.activateUrl);
    },

    handleContinueDelete() {
      if (this.deactivateAction) {
        this.deactivateAction();
      } else if (this.deactivateUrl) {
        this.openExternalLink(this.deactivateUrl);
      }
    },

    handleReactivate() {
      EventHelper.triggerReactivateSyncClicked({
        resourceName: this.parentType,
      });

      if (this.reactivateAction) {
        this.reactivateAction();
      } else if (this.reactivateUrl) {
        this.openExternalLink(this.reactivateUrl);
      }
    },
  },
};
</script>
<style module>
@import '../../css/sections.css';

.reactivateButton.reactivateButton {
  margin-right: 10px;
}

.timerIcon {
  height: 21px;
  margin-right: 7px;
  vertical-align: bottom;
  width: 21px;
}

.info.info {
  margin-top: 1.3rem;
}
</style>
